import React from 'react';
import { useTranslation } from 'react-i18next';

import { PiWarningBold, PiMoneyBold } from 'react-icons/pi';
import { motion } from 'framer-motion';

import { formatCurrency } from '@/utils/currency';

import MotionDiv from '@/components/common/Motion/MotionDiv';

import IncomeWidget from '../Widget/IncomeWidget';
import ExpensesWidget from '../Widget/ExpensesWidget';

interface ExpenseBarProps {
  current: number;
  total: number;
}

const iconStyle = {
  width: '0.92306rem',
  height: '0.92306rem'
};

const containerStyle = {
  width: '1.5rem',
  height: '1.5rem'
};

const ExpenseBar: React.FC<ExpenseBarProps> = ({ current, total }) => {
  const { t } = useTranslation();

  const percentage = total ? Math.min(100, (current / total) * 100) : 0;
  const formattedPercentage = percentage.toFixed(0);

  const noIncome = Math.round(total) === 0;
  const isOverTotal = current > total;
  const isBelow30 = !noIncome && Math.round(percentage) < 30;
  const isGrayZone = Math.round(percentage) >= 30 && Math.round(percentage) < 70;
  const warningZoneWidth = isBelow30 ? (30 - percentage) : 0;
  const grayZoneWidth = isGrayZone ? (70 - percentage) : 0;

  return (
    <div className={`w-full ${!noIncome && 'mt-6'}`}>
      <div className="relative">
        <MotionDiv delay={0.1} className="tour-expense-bar h-7 bg-gray-100 rounded-lg overflow-hidden relative">
          <div className="absolute inset-0">
            <div className="absolute left-[25%] top-1 bottom-1">
              <div className="w-px h-full bg-gray-200" />
            </div>
            <div className="absolute left-[50%] top-1 bottom-1">
              <div className="w-px h-full bg-gray-200" />
            </div>
            <div className="absolute left-[75%] top-1 bottom-1">
              <div className="w-px h-full bg-gray-200" />
            </div>
          </div>

          <motion.div
            className="h-full bg-blue-500 relative rounded-xs"
            initial={{ width: 0 }}
            animate={{ width: `${percentage}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <motion.div
              className="h-full flex items-center justify-center px-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <span className="text-white text-center text-sm font-semibold w-full">
                {percentage > 13 ? `${formattedPercentage}%` : ''}
              </span>
            </motion.div>
          </motion.div>

          {isBelow30 && (
            <motion.div
              className="absolute top-0 h-full"
              style={{
                left: `${percentage}%`,
                width: `${warningZoneWidth}%`,
                backgroundImage: `repeating-linear-gradient(
                  45deg,
                  rgba(251, 198, 75, 0.3),
                  rgba(251, 198, 75, 0.3) 0.0625rem,
                  transparent 0.0625rem,
                  transparent 0.375rem
                )`,
                backgroundSize: '0.5rem 0.5rem'
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            />
          )}

          {isGrayZone && (
            <motion.div
              className="absolute top-0 h-full"
              style={{
                left: `${percentage}%`,
                width: `${grayZoneWidth}%`,
                backgroundImage: `repeating-linear-gradient(
                  45deg,
                  rgba(216, 218, 222, 0.3),
                  rgba(216, 218, 222, 0.3) 0.0625rem,
                  transparent 0.0625rem,
                  transparent 0.375rem
                )`,
                backgroundSize: '0.5rem 0.5rem'
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            />
          )}
        </MotionDiv>
        {
          isBelow30 && <>
            <motion.div
              className="absolute left-[30%] w-[2px] bg-[#FBC64B] rounded-full"
              initial={{ height: 0, top: "50%" }}
              animate={{ height: "4rem", top: "-2rem" }}
              transition={{ duration: 0.3, delay: 0.4 }}
            />
            <MotionDiv
              className="absolute -top-8 left-[30%]"
              delay={0.4}
            >
              <div className="bg-[#FBC64B] rounded-full -translate-x-[0.78rem] flex items-center justify-center h-6 pl-[0.38rem] pr-2 gap-[0.38rem]">
                <PiWarningBold style={iconStyle} className="text-zinc-700" />
                <span className='font-semibold text-xs text-zinc-700'>{t('expenses.expensesBelow30')}</span>
              </div>
            </MotionDiv>
          </>
        }
        {isGrayZone && <>
          <motion.div
            className="absolute left-[70%] w-[2px] bg-[#D8DADE] rounded-full"
            initial={{ height: 0, top: "50%" }}
            animate={{ height: "4rem", top: "-2rem" }}
            transition={{ duration: 0.3, delay: 0.4 }}
          />
          <MotionDiv
            className="absolute -top-8 left-[70%]"
            delay={0.4}
          >
            <div className="bg-[#D8DADE] rounded-full translate-x-[calc(-50%+0.03125rem)] flex items-center justify-center" style={containerStyle}>
              <PiMoneyBold style={iconStyle} className="text-zinc-700" />
            </div>
          </MotionDiv>
        </>
        }
      </div>

      <MotionDiv className="flex items-center mt-4 mb-6" delay={0.2}>
        <div className="h-1.5 w-1.5 rounded-full bg-blue-500 mr-2" />
        <span className="font-medium text-xs text-zinc-700 mr-1">
          {t('expenses.actualExpenses')}:
        </span>
        <span className="font-semibold text-xs text-black">
          {formatCurrency.format(current)}
        </span>
      </MotionDiv>
      {
        isOverTotal && <IncomeWidget
          title={t('expenses.spendingMoreThanEarning')}
          text={t('income.addIncome')}
          to='/income'
        />
      }
      {
        !isOverTotal && noIncome && <IncomeWidget
          title={t('income.startAccounting')}
          text={t('income.start')}
          to='/bolt/balance'
          buttonVariant='primary'
        />
      }
      {
        !isOverTotal && !noIncome && <ExpensesWidget />
      }
    </div>
  );
};

export default ExpenseBar;